import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2, Inbox } from 'lucide-react';
import api from '../services/api';
import { useGroup } from '../context/AuthContext';

export function Logo({ size = 48, className = '' }) {
  const { group } = useGroup() || {};
  return (
    <img src={group?.logoUrl || '/logo.png'} alt={group?.name || 'Logo'} width={size} height={size}
      className={`rounded-lg object-contain ${className}`} style={{ width: size, height: size }} />
  );
}

export function Spinner({ className = '', size = 20 }) {
  return <Loader2 size={size} className={`animate-spin ${className}`} />;
}

export function PageLoader() {
  return <div className="flex min-h-[40vh] items-center justify-center text-gray-400"><Spinner size={28} /></div>;
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="font-display text-3xl font-bold text-navy-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Modal({ title, onClose, children, wide, footer }) {
  useEffect(() => {
    const onKey = e => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-navy-950/60 p-4 sm:items-center">
      <div className={`my-8 w-full rounded-xl bg-white shadow-xl ${wide ? 'max-w-3xl' : 'max-w-lg'}`}>
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-lg font-semibold text-navy-900">{title}</h2>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t bg-gray-50 px-5 py-3 rounded-b-xl">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, hint, error, required, children, className = '' }) {
  return (
    <div className={className}>
      {label && <label className="label">{label}{required && <span className="text-red-500"> *</span>}</label>}
      {children}
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

export function Empty({ icon: Icon = Inbox, title, children }) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-12 text-center text-gray-500">
      <Icon size={36} className="mb-3 text-gray-300" />
      <p className="font-medium text-gray-700">{title}</p>
      {children && <div className="mt-1 text-sm">{children}</div>}
    </div>
  );
}

const BADGE = {
  ACTIVE: 'bg-green-100 text-green-800',
  PENDING: 'bg-amber-100 text-amber-800',
  DISABLED: 'bg-gray-200 text-gray-700',
  DRAFT: 'bg-gray-100 text-gray-700',
  SENDING: 'bg-blue-100 text-blue-800',
  SENT: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-700',
  QUEUED: 'bg-gray-100 text-gray-700',
};
const BADGE_LABEL = { PENDING: 'Unverified', ACTIVE: 'Active', DISABLED: 'Disabled' };

export function StatusBadge({ status, label }) {
  return <span className={`badge ${BADGE[status] || 'bg-gray-100 text-gray-700'}`}>{label || BADGE_LABEL[status] || status}</span>;
}

export const CATEGORY_LABELS = {
  AGM: 'AGM', EVENT: 'Event', PROJECT: 'School project', GENERAL: 'General', DOCUMENT: 'Document', BIRTHDAY: 'Birthday',
};
const CATEGORY_COLORS = {
  AGM: 'bg-navy-100 text-navy-800', EVENT: 'bg-purple-100 text-purple-800', PROJECT: 'bg-emerald-100 text-emerald-800',
  GENERAL: 'bg-gray-100 text-gray-700', DOCUMENT: 'bg-sky-100 text-sky-800', BIRTHDAY: 'bg-pink-100 text-pink-800',
};
export function CategoryBadge({ category }) {
  return <span className={`badge ${CATEGORY_COLORS[category]}`}>{CATEGORY_LABELS[category] || category}</span>;
}

export function Toggle({ checked, onChange, label, description, disabled }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
      <button type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)}
        className={`relative mt-0.5 inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${checked ? 'bg-navy-800' : 'bg-gray-300'}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </button>
      <span>
        <span className="block text-sm font-medium text-gray-800">{label}</span>
        {description && <span className="block text-xs text-gray-500">{description}</span>}
      </span>
    </label>
  );
}

export function ConfirmDialog({ title, message, confirmLabel = 'Confirm', danger, onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await onConfirm(); onClose(); } finally { setBusy(false); }
  };
  return (
    <Modal title={title} onClose={onClose} footer={<>
      <button className="btn-outline" onClick={onClose}>Cancel</button>
      <button className={danger ? 'btn-danger' : 'btn-primary'} onClick={go} disabled={busy}>{busy && <Spinner size={16} />}{confirmLabel}</button>
    </>}>
      <p className="text-sm text-gray-600">{message}</p>
    </Modal>
  );
}

// Loads an image from an authenticated endpoint
export function AuthImage({ src, alt, className }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let href;
    let alive = true;
    setFailed(false);
    api.get(src, { responseType: 'blob' })
      .then(r => { if (!alive) return; href = URL.createObjectURL(r.data); setUrl({ href, type: r.data.type }); })
      .catch(() => alive && setFailed(true));
    return () => { alive = false; if (href) URL.revokeObjectURL(href); };
  }, [src]);
  if (failed) return <div className={`flex items-center justify-center bg-gray-100 text-xs text-gray-400 ${className}`}>No image</div>;
  if (!url) return <div className={`flex items-center justify-center bg-gray-50 ${className}`}><Spinner className="text-gray-300" /></div>;
  if (url.type === 'application/pdf') return <a href={url.href} target="_blank" rel="noreferrer" className="text-sm text-navy-700 underline">Open card (PDF)</a>;
  return <a href={url.href} target="_blank" rel="noreferrer"><img src={url.href} alt={alt} className={className} /></a>;
}

// Renders full email HTML in an isolated frame
export function EmailPreview({ html, height = 640 }) {
  return (
    <iframe title="Email preview" srcDoc={html || ''} sandbox="allow-same-origin allow-popups"
      className="w-full rounded-lg border border-gray-200 bg-[#EEF1F6]" style={{ height }} />
  );
}

// Select several values from a list, shown as chips
export function ChipSelect({ options, value, onChange, placeholder = 'Any' }) {
  const toggle = v => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
  if (!options.length) return <p className="text-xs text-gray-400">No values yet</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(o => (
        <button key={o} type="button" onClick={() => toggle(o)}
          className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${value.includes(o) ? 'border-navy-800 bg-navy-800 text-white' : 'border-gray-300 bg-white text-gray-600 hover:border-navy-400'}`}>
          {o}
        </button>
      ))}
      {!value.length && <span className="self-center text-xs text-gray-400">({placeholder})</span>}
    </div>
  );
}

export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="mb-5 flex gap-1 overflow-x-auto border-b border-gray-200">
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)}
          className={`-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${active === t.id ? 'border-gold-500 text-navy-900' : 'border-transparent text-gray-500 hover:text-navy-700'}`}>
          {t.icon && <t.icon size={16} />}{t.label}
        </button>
      ))}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, tone = 'navy', hint }) {
  const tones = {
    navy: 'bg-navy-50 text-navy-800', gold: 'bg-gold-50 text-gold-800', green: 'bg-green-50 text-green-700',
    amber: 'bg-amber-50 text-amber-700', red: 'bg-red-50 text-red-700', gray: 'bg-gray-100 text-gray-600',
  };
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{label}</p>
        {Icon && <span className={`rounded-lg p-2 ${tones[tone]}`}><Icon size={18} /></span>}
      </div>
      <p className="mt-2 text-3xl font-semibold text-navy-900">{value ?? '—'}</p>
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

// File picker with name display
export function FileInput({ accept, onChange, file, existing, label = 'Choose file' }) {
  const ref = useRef();
  return (
    <div className="flex flex-wrap items-center gap-3">
      <button type="button" className="btn-outline btn-sm" onClick={() => ref.current.click()}>{label}</button>
      <span className="truncate text-sm text-gray-600">{file?.name || existing || 'No file chosen'}</span>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={e => { onChange(e.target.files[0] || null); e.target.value = ''; }} />
    </div>
  );
}

export const fmtDate = d => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
export const fmtDateTime = d => (d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
export const fmtSize = b => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
export const dateInput = d => (d ? new Date(d).toISOString().slice(0, 10) : '');
